import {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { Auth } from "src/domain_v2/auth";
import { ROUTE_NAME } from "src/RouteName";

import { maxios } from "./index";

// 토큰 갱신 중복 방지를 위한 Promise 캐시
let refreshTokenPromise: Promise<void> | null = null;

/**
 * 모든 Axios 인터셉터 설정
 */
export const setupInterceptors = () => {
  setupAuthInterceptor();
  // 추가 인터셉터를 여기에 등록 가능
  // setupLoggingInterceptor();
  // setupErrorInterceptor();
};

/**
 * 인증 인터셉터 설정
 * - 자동 토큰 갱신
 * - 401/403 에러 핸들링
 */
function setupAuthInterceptor() {
  // 요청 인터셉터 - 토큰 자동 갱신
  maxios.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // 인증 불필요 엔드포인트는 건너뛰기
      const publicEndpoints = [
        "/password/reset-token",
        "/v2/login",
        "/v2/reissue",
        "/v2/logout",
        "/v2/x-auth-token-to-jwt",
      ];
      if (publicEndpoints.some((endpoint) => config.url?.includes(endpoint))) {
        return config;
      }

      // 토큰 정보 확인
      let tokenInfo = Auth.authService.tokenInfo;

      // 토큰이 없으면 레거시 마이그레이션 시도
      if (!tokenInfo) {
        tokenInfo = await Auth.authService.migrateLegacyToken();
      }

      // 여전히 토큰이 없으면 그냥 진행
      if (!tokenInfo) {
        return config;
      }

      // 토큰 갱신이 필요한 경우
      if (Auth.authService.needsRefresh) {
        // 이미 갱신 중이면 대기
        if (refreshTokenPromise) {
          await refreshTokenPromise;
        } else {
          // 새로운 갱신 시작
          refreshTokenPromise = refreshAccessToken();
          try {
            await refreshTokenPromise;
          } finally {
            refreshTokenPromise = null;
          }
        }

        // 갱신된 토큰 다시 가져오기
        tokenInfo = Auth.authService.tokenInfo;
      }

      // Authorization 헤더 설정 (Bearer prefix 포함)
      if (tokenInfo && config.headers) {
        config.headers["Authorization"] = `Bearer ${tokenInfo.accessToken}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // 응답 인터셉터 - 401 에러 핸들링
  maxios.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: boolean;
      };

      // 401 에러이고 재시도하지 않은 경우
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // 토큰 갱신 시도
          if (refreshTokenPromise) {
            await refreshTokenPromise;
          } else {
            refreshTokenPromise = refreshAccessToken();
            try {
              await refreshTokenPromise;
            } finally {
              refreshTokenPromise = null;
            }
          }

          // 새 토큰으로 재시도 (Bearer prefix 포함)
          const tokenInfo = Auth.authService.tokenInfo;
          if (tokenInfo && originalRequest.headers) {
            originalRequest.headers[
              "Authorization"
            ] = `Bearer ${tokenInfo.accessToken}`;
          }

          return maxios(originalRequest);
        } catch (refreshError) {
          // 갱신 실패 시 로그아웃
          handleAuthFailure();
          return Promise.reject(refreshError);
        }
      }

      // 403 또는 다른 인증 실패
      if (error.response?.status === 403) {
        handleAuthFailure();
      }

      return Promise.reject(error);
    }
  );
}

/**
 * 토큰 갱신 로직
 */
async function refreshAccessToken(): Promise<void> {
  await Auth.authService.reissue();
}

/**
 * 인증 실패 처리
 */
function handleAuthFailure(): void {
  Auth.authService.clearToken();

  // 로그인 페이지로 리다이렉트 (현재 위치가 로그인이 아닌 경우만)
  if (window.location.pathname !== ROUTE_NAME.LOGIN) {
    window.location.pathname = ROUTE_NAME.LOGIN;
  }
}
